import {
  BlockMapBuilder,
  ContentBlock,
  Modifier,
  EditorState,
  genKey,
  CharacterMetadata,
  RichUtils,
} from "draft-js";

import { List, Repeat } from "immutable";

import forceEditorSelection from "editor/utils/forceEditorSelection";
import { BLOCK_TYPE } from "config/constants/editor";

//this is a dynamic adaptation of draft's AtomicBlockUtils
export const CustomBlockUtils = {
  insertCustomBlocks(editorState, fragments, options = {}) {
    const defaultOptions = {
      selectionIndex: 0,
      selectionOffset: "end",
      safetyBlock: true,
      forceSplit: false,
      forceNoSplit: false,
      removeFullBlocks: false,
    };
    const insertionOptions = {
      ...defaultOptions,
      ...options,
    };

    const contentState = editorState.getCurrentContent();
    if (insertionOptions.removeFullBlocks) {
      editorState = forceEditorSelection(editorState, {
        anchorKey: insertionOptions.removeFullBlocks.startKey,
        anchorOffset: 0,
        focusKey: insertionOptions.removeFullBlocks.endKey,
        focusOffset: contentState
          .getBlockForKey(insertionOptions.removeFullBlocks.endKey)
          .getLength(),
      });
    }
    const selectionState = editorState.getSelection();
    const afterRemoval = Modifier.removeRange(
      contentState,
      selectionState,
      "backward"
    );
    const targetSelection = afterRemoval.getSelectionAfter();
    const anchorKey = selectionState.getAnchorKey();
    let afterSplit, insertionTarget;
    //blocks inserted on a newline that is not the first should not have an empty line before them
    if (
      insertionOptions.forceNoSplit ||
      (selectionState.isCollapsed() &&
        !contentState.getBlockForKey(anchorKey).getText() &&
        contentState.getFirstBlock().getKey() !== anchorKey &&
        !insertionOptions.forceSplit)
    ) {
      afterSplit = afterRemoval;
      insertionTarget = targetSelection;
    } else {
      afterSplit = Modifier.splitBlock(afterRemoval, targetSelection);
      insertionTarget = afterSplit.getSelectionAfter();
    }

    const defaultBlock = {
      type: BLOCK_TYPE.UNSTYLED,
      text: "",
    };
    const charData = CharacterMetadata.create();

    /**
     * text after split should be in new block, not inserted block
     * there must be a block beneath non-editable blocks so you can keep typing
     * draft coerces single-block insertions to the type of the previous block
     * so unless you can guarantee you are always inserting more than one block, this should be true
     */
    if (insertionOptions.safetyBlock) {
      fragments.push({});
    }
    const fragmentArray = fragments.map(block => {
      const newBlock = {
        ...defaultBlock,
        characterList: block.text
          ? List(Repeat(charData, block.text.length))
          : List(),
        ...block,
        key: genKey(),
      };
      return new ContentBlock(newBlock);
    });

    const fragment = BlockMapBuilder.createFromArray(fragmentArray);
    const withCustomBlocks = Modifier.replaceWithFragment(
      afterSplit,
      insertionTarget,
      fragment
    );

    let newSelection;
    if (insertionOptions.selectionIndex === "after") {
      newSelection = withCustomBlocks.getSelectionAfter().set("hasFocus", true);
    } else {
      const startKey = selectionState.getStartKey();
      //iterate over the block map until at the block insertion happened at,
      //then keep going until reaching the designated block to get key
      const focusKey = withCustomBlocks
        .getBlockMap()
        .skipUntil(block => block.getKey() === startKey)
        .take(insertionOptions.selectionIndex + 2)
        .last()
        .getKey();
      const focusOffset =
        insertionOptions.selectionOffset === "end"
          ? withCustomBlocks.getBlockForKey(focusKey).getText().length
          : insertionOptions.selectionOffset;
      newSelection = selectionState.merge({
        focusKey,
        anchorKey: focusKey,
        hasFocus: true,
        focusOffset,
        anchorOffset: focusOffset,
      });
    }

    const newContent = withCustomBlocks.merge({
      selectionBefore: selectionState,
      selectionAfter: newSelection,
    });

    return EditorState.push(editorState, newContent, "insert-fragment");
  },

  removeBlockByKey(editorState, key) {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    /**
     * If block to be removed is first block, anchorBlock will be null
     * In that case, remove all the block text and toggle it to unstyled
     */
    const anchorBlock = contentState.getBlockBefore(key);

    const newSelectionState = selectionState.merge({
      anchorKey: anchorBlock ? anchorBlock.getKey() : key,
      anchorOffset: anchorBlock ? anchorBlock.getLength() : 0,
      focusKey: key,
      focusOffset: contentState.getBlockForKey(key).getLength(),
      isBackward: false,
    });

    const newContentState = Modifier.removeRange(
      contentState,
      newSelectionState,
      "forward"
    );
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      "remove-range"
    );

    return anchorBlock
      ? newEditorState
      : RichUtils.toggleBlockType(newEditorState, BLOCK_TYPE.UNSTYLED);
  },
};
